/**
 * NOMINATE — the phone's bottom sheet over the round page (3039; port of
 * `design/GT Mobile Round.dc.html`, the `inModals` half). A section would put
 * 23 covers below the fold and mean scrolling back up to the wheel after
 * every nomination; the sheet closes onto it.
 *
 * Fixed 560px panel: static head (title, count, ✕), the FILTER trigger (the
 * phone's filter is the shell-owned FiltersSheet — this only opens it), the
 * tier tab strip, ONE scrolling 3-up grid, and a footer with the quarter
 * pocket, TAKE BACK and DONE. Tapping a cover is the whole affordance — no
 * chip, no hover; the coin landing on the cover is the feedback.
 */

import { CoverArt } from '../components/CoverArt.tsx';
import { PixelButton } from '../components/PixelButton.tsx';
import { QuarterIcon } from '../components/QuarterIcon.tsx';
import { SearchBox } from '../components/SearchBox.tsx';
import { hueSplit, NominatorStack } from '../components/Wheel.tsx';
import { useArt } from '../components/useArt.ts';
import { Pips } from '../screens/Round.tsx';
import { useQd } from '../store/index.ts';
import { roundVM } from '../store/selectors.round.ts';
import type { RoundVM } from '../store/selectors.round.ts';
import { tonightVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

type Tile = RoundVM['tiles'][number];

function Cover({ g }: { g: Tile }): JSX.Element {
  const rNom = useQd((s) => s.rNom);
  const art = useArt(g.id + 'p', g.coverCands);
  return (
    <div
      onClick={g.act ? () => void rNom(g.id) : undefined}
      // 3px when a quarter is on it (Marcus, staging 2026-08-26: the 1px hue
      // outline did not read as "held"); 1px otherwise. Same weight the pin
      // card already uses for the same fact.
      style={{ display: 'flex', flexDirection: 'column', minWidth: 0, border: (g.heldN ? 3 : 1) + 'px solid ' + g.bd, ...hueSplit(g.bdHues), background: 'var(--gt-tile)', cursor: g.cur }}
    >
      <div style={{ position: 'relative', aspectRatio: '2/3', background: g.fall }}>
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={12} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
        {/* my coin, then every other backer (3054) — 18px here, not 22: three
            faces have to fit a third of a 360px phone */}
        {g.heldN ? (
          <div style={{ position: 'absolute', left: 5, top: 5, zIndex: 4, display: 'flex', alignItems: 'center', gap: g.coin && g.avs.length ? 3 : 0 }}>
            {g.coin ? <QuarterIcon hover={false} u={1.7} sparkles={false} auto={false} /> : null}
            <NominatorStack bys={g.avs} more={g.avMore} size={18} fs={7} />
          </div>
        ) : null}
      </div>
      <div style={{ padding: '6px 7px', fontFamily: MONO, fontSize: '8.5px', color: 'var(--gt-text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.nm}</div>
    </div>
  );
}

export default function NominateSheet(): JSX.Element | null {
  const s = useQd();
  const v = roundVM(s, s.serverNow());
  const t = tonightVM(s);
  if (!v) return null;
  const close = () => s.rSheetClose();

  return (
    <div
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) close();
      }}
      data-screen-label="Nominate sheet"
      style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', top: 0, bottom: 0, width: '100%', maxWidth: 430, zIndex: 75, background: 'var(--gt-overlay)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      {/* DEVIATION from the mirror's fixed 560px panel (Marcus, staging
          2026-08-26: "too cramped"): the sheet takes most of the screen, so a
          72-game crew list shows three rows of covers instead of one and a
          half. Owes an upstream DESIGN-CHANGELOG entry. */}
      <div style={{ width: '100%', height: 'calc(92dvh - env(safe-area-inset-bottom))', maxHeight: 'calc(100dvh - 48px)', overflow: 'hidden', background: 'var(--gt-card)', borderTop: '1px solid var(--gt-border)', boxShadow: '0 -10px 0 rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', animation: 'gt-sheet .18s ease-out' }}>
        {/* head ------------------------------------------------------------- */}
        <div style={{ display: 'flex', flex: 'none', flexDirection: 'column', borderBottom: '1px solid var(--gt-line)', background: 'var(--gt-card)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px' }}>
            <span style={{ fontFamily: PIXEL, fontSize: 9, color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' }}>NOMINATE</span>
            <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>{v.hatTxt}</span>
            <div style={{ flex: 1 }} />
            <div onClick={close} style={{ width: 44, height: 44, margin: '-8px -8px -8px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: 13, color: 'var(--gt-ghost)', cursor: 'pointer' }}>
              ✕
            </div>
          </div>
          {/* BOX above the filter trigger, both full width — the phone reads the
              pair top to bottom (DESIGN-CHANGELOG 2026-08-28). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 14px 12px' }}>
            <SearchBox
              value={v.bq}
              onChange={(e) => s.setBq('round', e.target.value)}
              onClear={() => s.setBq('round', '')}
              placeholder="SEARCH THE RAIL…"
              w="100%"
              ifs={12}
              pad="13px 12px"
            />
            <div
              onClick={() => s.toggleFltOpen()}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid ' + t.fltBtnBd, color: t.fltBtnClr, background: t.fltBtnBg, fontFamily: MONO, fontSize: '10.5px', height: 44, cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box' }}
            >
              {t.fltBtnTxt}
            </div>
          </div>
          <div data-hs="1" style={{ display: 'flex', overflowX: 'auto', padding: '0 14px 12px' }}>
            {v.tabs.map((tb) => (
              <div
                key={tb.key}
                onClick={() => s.rSetTier(tb.key)}
                style={{ marginLeft: tb.ml, position: tb.pos, zIndex: tb.z as number, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 44, padding: '0 14px', border: '1px solid ' + tb.bd, background: tb.bg, color: tb.clr, fontFamily: MONO, fontSize: 10, letterSpacing: '.06em', cursor: 'pointer', whiteSpace: 'nowrap', flex: 'none', boxSizing: 'border-box' }}
              >
                {tb.t}
                <span style={{ color: tb.nClr }}>{tb.n}</span>
              </div>
            ))}
          </div>
        </div>

        {/* grid ------------------------------------------------------------- */}
        <div data-hs="1" style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 14px 16px' }}>
          {v.railEmpty ? (
            <div style={{ border: '1px dashed var(--gt-border-3)', padding: 18, textAlign: 'center', fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.06em', lineHeight: 1.9, color: 'var(--gt-muted)' }}>{v.railEmptyTxt}</div>
          ) : null}
          {/* minmax(0,1fr), not 1fr: a cell's min-content is its nowrap title,
              and a bare 1fr let the widest name blow the whole grid past the
              sheet's edge into a horizontal scroll (Marcus, staging 2026-08-26). */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, width: '100%' }}>
            {v.tiles.map((g) => (
              <Cover key={g.key} g={g} />
            ))}
          </div>
        </div>

        {/* footer ----------------------------------------------------------- */}
        <div style={{ display: 'flex', flex: 'none', alignItems: 'center', gap: 10, padding: '12px 14px 16px', borderTop: '1px solid var(--gt-line)', background: 'var(--gt-card)' }}>
          <Pips pips={v.pips} size={24} />
          {v.clrOn ? (
            <div onClick={() => void s.rClearMine()} style={{ border: '1px solid var(--acc2)', color: 'var(--acc2)', fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', padding: '9px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {v.clrLbl}
            </div>
          ) : null}
          <div style={{ flex: 1 }} />
          <PixelButton label="DONE ▸" fs={9} pad="14px 20px" nowrap fit lh="1" onClick={close} />
        </div>
      </div>
    </div>
  );
}
